import { makeObservable, action, observable } from 'mobx'
import { AlertFactory } from '../alerts/Alert'
import { OpportunitiesPort, JobStatsPort, IamPort } from './ports'
import { GetCurrentMember } from '../iam/SessionStore'
import { Router } from '../types/definitions'
import { Observable, from, Subscription } from 'rxjs'
import JobEvent from '../jobs/JobEvent'
import Member from '../iam/models/Member'
import UserDataStore from '../iam/UserDataStore'
import WorkspaceMembershipStore from '../iam/WorkspaceMembershipStore'
import { OpportunityOfferStore } from './offers/OpportunityOfferStore'
import ConsultationModalViewModel from './views/ConsultationModal/ConsultationModal'
import WorkspaceOpportunitiesAdapter from './workspace-postings-opportunities/WorkspaceOpportunitiesAdapter'
import TabController from '../infra/TabController'
import OpportunitiesViewModel from './OpportunitiesViewModel'
import WorkspacePostingOpportunitiesViewModel from './workspace-postings-opportunities/WorkspacePostingOpportunitiesViewModel'
import { task } from 'mobx-task'
import { tap } from 'rxjs/operators'
import { shareLatest } from '../utils/rx-util'
import link from '../infra/link'
import WorkspaceOpportunityInvitationStore from './invitations/WorkspaceOpportunityInvitationStore'

export default class OpportunitiesRootViewModel {
  /**
   * TabController for the Opportunities tabs.
   */
  opportunitiesTabController = new TabController<OpportunitiesTabs>(() => [
    {
      tab: OpportunitiesTabs.Jobs,
      title: 'Available jobs',
      hasError: () => false,
    },
    {
      tab: OpportunitiesTabs.Workspaces,
      title: 'Outsourcing posts',
      hasError: () => false,
    },
  ])

  opportunitiesViewModel = new OpportunitiesViewModel(
    this.opportunitiesPort,
    this.jobStatsPort,
    this.iamPort,
    this.getCurrentMember,
    this.newAlert,
    this.router,
    this.jobEvents$,
    this.currentMember$,
    this.userDataStore,
    this.workspaceMembershipStore,
    this.opportunityOffersStore,
    this.consultationModalViewModel,
  )

  workspaceOpportunitiesViewModel = this.workspacePostingOpportunitiesPort
    ? new WorkspacePostingOpportunitiesViewModel(
        this.workspacePostingOpportunitiesPort,
        this.newAlert,
        this.workspaceOpportunityInvitationStore,
        this.workspaceMembershipStore,
      )
    : null

  subscription: Subscription = new Subscription()

  showTabs = false

  constructor(
    private opportunitiesPort: OpportunitiesPort,
    private jobStatsPort: JobStatsPort,
    private iamPort: IamPort,
    private getCurrentMember: GetCurrentMember,
    private newAlert: AlertFactory,
    private router: Router,
    private jobEvents$: Observable<JobEvent>,
    private currentMember$: Observable<Member>,
    private userDataStore: UserDataStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private opportunityOffersStore: OpportunityOfferStore,
    public consultationModalViewModel: ConsultationModalViewModel,
    private workspacePostingOpportunitiesPort: WorkspaceOpportunitiesAdapter | null,
    private workspaceOpportunityInvitationStore: WorkspaceOpportunityInvitationStore | null,
  ) {
    makeObservable(this, {
      activate: action.bound,
      showTabs: observable,
    })
  }

  activate() {
    this.subscription = from(this.canViewOps())
      .pipe(
        shareLatest(),
        tap(([canViewOps, canViewWorkspaceOps]) => {
          if (canViewOps) {
            this.opportunitiesTabController.change(OpportunitiesTabs.Jobs)
          } else if (canViewWorkspaceOps) {
            this.opportunitiesTabController.change(OpportunitiesTabs.Workspaces)
          } else {
            this.router.push(link.jobs())
          }
          this.showTabs = canViewOps && canViewWorkspaceOps
        }),
      )
      .subscribe()
  }

  deactivate() {
    this.subscription.unsubscribe()
  }

  canViewOps = task(
    async (): Promise<[boolean, boolean]> =>
      Promise.all([
        this.opportunitiesPort.probeCanViewOpportunities(),
        this.workspacePostingOpportunitiesPort
          ? this.workspacePostingOpportunitiesPort.canViewWorkspaceOpportunities()
          : false,
      ]),
  )
}

/**
 * Documents section tab.
 */
export const enum OpportunitiesTabs {
  Jobs,
  Workspaces,
}
